import GeoJSON from 'ol/format/GeoJSON';
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader';
import GeoJSONWriter from 'jsts/org/locationtech/jts/io/GeoJSONWriter';
import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp';
import { Style } from 'ol/style';
import { LINE_STRING, MULTI_LINE_STRING } from 'client/utils/constants/geometryType';

const geoJsonFormat = new GeoJSON();
const jstsReader = new GeoJSONReader();
const jstsWriter = new GeoJSONWriter();

export const createBufferedPolygonFromLine = (lineGeometry, bufferDistanceMapUnits) => {
    const lineGeoJson = geoJsonFormat.writeGeometryObject(lineGeometry);
    const jstsLine = jstsReader.read(lineGeoJson);
    const jstsBuffered = BufferOp.bufferOp(jstsLine, bufferDistanceMapUnits);
    const bufferedGeoJson = jstsWriter.write(jstsBuffered);

    return geoJsonFormat.readGeometry(bufferedGeoJson);
};

/**
 * Builds an OpenLayers style array for rendering a buffered geometry.
 *
 * This is primarily used for the "linear search" AOI mode where a LineString is visually
 * represented as a buffered polygon band (fill + dashed border) plus an optional center stroke.
 *
 * Performance notes:
 * - Buffering uses JSTS, which can be relatively expensive.
 * - This implementation intentionally does not cache buffered geometries.
 *
 * Failure behavior:
 * - If buffering fails (JSTS edge cases), the buffer polygon style returns `null` geometry.
 * - If `fallbackBufferStroke` is provided, it draws a fallback stroke on the original line.
 */
export const createBufferedStyles = ({
    bufferRadiusMetres,
    bufferFill,
    bufferBorderStroke,
    centerStroke,
    fallbackBufferStroke
}) => {
    // Cache buffered results per geometry revision so we don't recompute buffering
    // multiple times for the same feature during a single render.
    //
    // Keying by geometry (WeakMap) avoids leaks when features/geometries are removed.
    const bufferCache = new WeakMap();

    const getBufferedResult = (lineGeometry) => {
        const revision = typeof lineGeometry?.getRevision === 'function' ? lineGeometry.getRevision() : undefined;
        const cached = bufferCache.get(lineGeometry);

        if (cached && cached.revision === revision) {
            return cached;
        }

        try {
            const buffered = createBufferedPolygonFromLine(lineGeometry, bufferRadiusMetres);
            const next = { revision, ok: true, buffered };
            bufferCache.set(lineGeometry, next);
            return next;
        } catch (e) {
            const next = { revision, ok: false, buffered: null };
            bufferCache.set(lineGeometry, next);
            return next;
        }
    };

    const bufferPolygonStyle = new Style({
        fill: bufferFill || undefined,
        stroke: bufferBorderStroke,
        geometry: (feature) => {
            const geometry = feature?.getGeometry?.();
            const geometryType = geometry?.getType?.();
            if (!geometry || (geometryType !== LINE_STRING && geometryType !== MULTI_LINE_STRING)) {
                return null;
            }

            const result = getBufferedResult(geometry);
            return result.ok ? result.buffered : null;
        }
    });

    const styles = [bufferPolygonStyle];

    if (fallbackBufferStroke) {
        styles.push(
            new Style({
                stroke: fallbackBufferStroke,
                geometry: (feature) => {
                    const geometry = feature?.getGeometry?.();
                    const geometryType = geometry?.getType?.();
                    if (!geometry || (geometryType !== LINE_STRING && geometryType !== MULTI_LINE_STRING)) {
                        return null;
                    }

                    // Only draw fallback when buffering fails.
                    const result = getBufferedResult(geometry);
                    return result.ok ? null : geometry;
                }
            })
        );
    }

    if (centerStroke) {
        styles.push(
            new Style({
                stroke: centerStroke
            })
        );
    }

    return styles;
};

export default createBufferedStyles;
